import queryString from 'query-string';
import cookie from 'cookie';
import jwtUtil from 'jsonwebtoken';
import { cookieNames } from 'constants';

const AUTH_COOKIE_NAME = cookieNames.authCookieName;
const IS_NODE =
  typeof document === 'undefined' ||
  (process && process.env && process.env.NODE_ENV === 'test');

let rawCookie = {};

function updateCookieExpiryDate({ expires = null }) {
  rawCookie.expires = expires;
}

function getCookieExpiryDate() {
  return rawCookie.expires;
}

function isAccessTokenUpdatedOnServer() {
  return Boolean(getCookieExpiryDate());
}

function parseAuthCookie() {
  const cookies = IS_NODE ? rawCookie : cookie.parse(document.cookie);
  return cookies[AUTH_COOKIE_NAME];
}

function loadAuthFromCookie() {
  const parsedCookie = parseAuthCookie();
  const parsedQuery = queryString.parse(`?${parsedCookie}`);

  return {
    accessToken: parsedQuery.access_token,
    refreshToken: parsedQuery.refresh_token,
    issued: parsedQuery['.issued'],
    expires: parsedQuery['.expires'],
  };
}

function decodeJWT(auth) {
  return auth.accessToken === 'ACCESS_TOKEN'
    ? undefined
    : jwtUtil.decode(auth.accessToken);
}

function initializeFromCookie(aCookie) {
  rawCookie = {};
  rawCookie[AUTH_COOKIE_NAME] = aCookie;
}

function initializeFromRequest(req) {
  if (req) {
    if (req.cookie) {
      rawCookie = req.cookie;
    } else if (req.cookies) {
      rawCookie = req.cookies;
    } else if (req.headers && req.headers.cookie) {
      rawCookie = cookie.parse(req.headers.cookie);
    } else {
      rawCookie = {};
    }
  }
}

function exists() {
  const auth = loadAuthFromCookie();
  return auth.accessToken !== undefined;
}

function isExpired(jwt = decodeJWT(loadAuthFromCookie())) {
  if (!jwt || !jwt.exp) {
    return true;
  }
  const expiryDate = jwt.exp;
  const cookieExpiryUnix = expiryDate * 1000;
  return cookieExpiryUnix <= new Date(Date.now() + 30000);
}

function getAccessToken() {
  const auth = loadAuthFromCookie();
  return auth.accessToken;
}

function getRefreshToken() {
  const auth = loadAuthFromCookie();
  return auth.refreshToken;
}

function getUserGuid() {
  const auth = loadAuthFromCookie();
  const jwt = decodeJWT(auth);

  return jwt ? jwt.sub : undefined;
}

function getAud() {
  const auth = loadAuthFromCookie();
  const jwt = decodeJWT(auth);

  return jwt ? jwt.aud : undefined;
}

function getUserRole() {
  const auth = loadAuthFromCookie();
  const jwt = decodeJWT(auth);

  return jwt ? jwt.role : undefined;
}

function getIsSuperUser() {
  return getUserRole() === 'superuser';
}

export default {
  updateCookieExpiryDate,
  isAccessTokenUpdatedOnServer,
  getCookieExpiryDate,
  parseAuthCookie,
  loadAuthFromCookie,
  getIsSuperUser,
  getUserRole,
  initializeFromCookie,
  initializeFromRequest,
  exists,
  isExpired,
  getAccessToken,
  getRefreshToken,
  getUserGuid,
  getAud,
};
